import { Box } from '@chakra-ui/layout';
import {
  type ClientRect,
  DndContext,
  type DragEndEvent,
  type DragMoveEvent,
  type DragStartEvent,
  type Modifier,
} from '@dnd-kit/core';
import type { Transform } from '@dnd-kit/utilities';
import clamp from 'lodash/clamp';
import { type PropsWithChildren, useCallback, useRef } from 'react';

import { usePageConfig } from '../PageConfigProvider/PageConfigProvider';
import { PageType } from '../PageConfigProvider/types';
import type {
  Position,
  PositionedItem,
} from '../PageConfigProvider/types/item';
import { MIN_ITEM_HEIGHT_CELLS, MIN_ITEM_WIDTH_CELLS } from './constants';
import { useGrid } from './GridProvider';
import { ResizeEdge } from './ResizeEdge';
import { validatePosition } from './utils/grid-layout-utils';
import { clampPositionToReportPage } from './utils/report-page-boundaries';

export type Direction = 'e' | 'w' | 'n' | 's' | 'ne' | 'nw' | 'se' | 'sw';

type ResizerProps = PropsWithChildren<{
  id: string;
  item: PositionedItem;
}>;

export const Resizer = ({ id, item, children }: ResizerProps) => {
  const {
    grid,
    gridItems,
    updateItemSize,
    edgeDragging,
    setEdgeDragging,
    setTempResize,
  } = useGrid();
  const { cellWidth, cellHeight, rows, columns } = grid;
  const { config: pageConfig } = usePageConfig();
  const isReport = pageConfig.type === PageType.REPORT;

  const computeResizedPosition = useCallback(
    (direction: Direction, deltaX: number, deltaY: number): Position => {
      const deltaXCells = Math.round(deltaX / cellWidth);
      const deltaYCells = Math.round(deltaY / cellHeight);

      const original = item.position;
      const rightEdge = original.x + original.width;
      const bottomEdge = original.y + original.height;

      let { x, y, width, height } = original;

      // Calculate new dimensions based on drag direction
      switch (direction) {
        case 'se':
          width += deltaXCells;
          height += deltaYCells;
          break;
        case 'ne':
          width += deltaXCells;
          height -= deltaYCells;
          break;
        case 'sw':
          width -= deltaXCells;
          height += deltaYCells;
          break;
        case 'nw':
          width -= deltaXCells;
          height -= deltaYCells;
          break;
      }

      width = clamp(width, MIN_ITEM_WIDTH_CELLS, columns);
      height = clamp(height, MIN_ITEM_HEIGHT_CELLS, rows);

      if (direction === 'nw' || direction === 'sw') {
        x = rightEdge - width;
      }
      if (direction === 'nw' || direction === 'ne') {
        y = bottomEdge - height;
      }

      // Clamp final position and dimensions to stay within the grid boundaries
      x = clamp(x, 0, columns - width);
      y = clamp(y, 0, rows - height);

      width = clamp(width, MIN_ITEM_WIDTH_CELLS, columns - x);
      height = clamp(height, MIN_ITEM_HEIGHT_CELLS, rows - y);

      let candidate: Position = { x, y, width, height };
      if (isReport) {
        candidate = clampPositionToReportPage(candidate, item.position.y);
      }

      if (validatePosition(grid, candidate, gridItems, item.id)) {
        return candidate;
      }

      for (let w = width; w >= MIN_ITEM_WIDTH_CELLS; w--) {
        for (let h = height; h >= MIN_ITEM_HEIGHT_CELLS; h--) {
          const testPosition: Position = {
            x: direction === 'nw' || direction === 'sw' ? rightEdge - w : x,
            y: direction === 'nw' || direction === 'ne' ? bottomEdge - h : y,
            width: w,
            height: h,
          };

          const boundedX = clamp(
            testPosition.x,
            0,
            columns - testPosition.width
          );
          const boundedY = clamp(testPosition.y, 0, rows - testPosition.height);
          let finalTestPosition: Position = {
            ...testPosition,
            x: boundedX,
            y: boundedY,
          };
          if (isReport) {
            finalTestPosition = clampPositionToReportPage(
              finalTestPosition,
              item.position.y
            );
          }

          if (validatePosition(grid, finalTestPosition, gridItems, item.id)) {
            return finalTestPosition;
          }
        }
      }

      return original;
    },
    [cellWidth, cellHeight, item, columns, rows, grid, gridItems, isReport]
  );

  const blockTempResize = useRef(true);

  const handleDragStart = (event: DragStartEvent) => {
    blockTempResize.current = false;
    const { active } = event;
    const direction = active.data.current?.direction as Direction | undefined;
    if (!direction) return;
    setEdgeDragging(direction);
  };

  const handleDragMove = (event: DragMoveEvent) => {
    const { active } = event;
    const direction = active.data.current?.direction as Direction | undefined;
    if (!direction || blockTempResize.current) return;

    const newPosition = computeResizedPosition(
      direction,
      event.delta.x,
      event.delta.y
    );

    setTempResize({
      id: item.id,
      x: (newPosition.x - item.position.x) * cellWidth,
      y: (newPosition.y - item.position.y) * cellHeight,
      width: (newPosition.width - item.position.width) * cellWidth,
      height: (newPosition.height - item.position.height) * cellHeight,
    });
  };

  const handleDiagonalDragEnd = (event: DragEndEvent) => {
    blockTempResize.current = true;
    setTempResize(null);
    setEdgeDragging(null);
    const { active, delta } = event;
    const direction = active.data.current?.direction as Direction | undefined;
    if (!direction) return;

    const newPosition = computeResizedPosition(direction, delta.x, delta.y);

    updateItemSize(item, newPosition);
  };

  return (
    <Box position="relative" width="100%" height="100%">
      {children}
      <DndContext
        onDragStart={handleDragStart}
        onDragMove={handleDragMove}
        onDragEnd={handleDiagonalDragEnd}
        modifiers={[restrictToElement('editable-grid-container')]}
        autoScroll={false}
      >
        <ResizeEdge id={id} direction="nw" activeResizeEdge={edgeDragging} />
        <ResizeEdge id={id} direction="ne" activeResizeEdge={edgeDragging} />
        <ResizeEdge id={id} direction="se" activeResizeEdge={edgeDragging} />
        <ResizeEdge id={id} direction="sw" activeResizeEdge={edgeDragging} />
      </DndContext>
    </Box>
  );
};

const restrictToElement: (id: string) => Modifier = (id) => (args) => {
  const element = document.getElementById(id);
  if (!element) return args.transform;

  const elementRect = element.getBoundingClientRect();

  if (!args.draggingNodeRect || !elementRect) {
    return args.transform;
  }

  return restrictToBoundingRect(
    args.transform,
    args.draggingNodeRect,
    elementRect
  );
};

export function restrictToBoundingRect(
  transform: Transform,
  rect: ClientRect,
  boundingRect: ClientRect
): Transform {
  const minX = boundingRect.left - rect.left;
  const maxX = boundingRect.right - rect.right;
  const minY = boundingRect.top - rect.top;
  const maxY = boundingRect.bottom - rect.bottom;

  return {
    ...transform,
    x: clamp(transform.x, minX, maxX),
    y: clamp(transform.y, minY, maxY),
  };
}
